import { useEffect } from "react";
import { Link } from "react-router-dom";
import ResellerBento from "../components/reseller-landing/ResellerBento";
import { useTranslation } from "../context/LanguageContext";
import useEnforceLightMode from "../hooks/useEnforceLightMode";
import usePageBackground from "../hooks/usePageBackground";
import { GUIDES } from "../seo/guides";
import { track, captureUtm } from "../utils/analytics";
import { LANDING_PAGE_PLANS } from "../utils/billingConfig";
import { helpCenterUrl } from "../utils/helpCenter";
import "./ResellerLandingPage.css";

// Reseller landing at "/" (approved design: Landing-B1-line). The page never names
// one retailer; it is covered by "Big Box"/"Big Box Retailers" instead.

const FIND_KEYS = [1, 2, 3, 4, 5, 6, 7].map((n) => `resellerLanding.find${n}`);

const STEPS = [
  { img: "/landing/illustrations/n2-alert.webp", n: 1 },
  { img: "/landing/illustrations/n3-trunk.webp", n: 2 },
  { img: "/landing/illustrations/n4-ship.webp", n: 3 },
  { img: "/landing/illustrations/n5-paid.webp", n: 4 },
];

const HAULS = [
  { img: "/landing/community/penny-vacuums.webp", n: 1 },
  { img: "/landing/community/coolers.webp", n: 2 },
  { img: "/landing/community/penny-hoses.webp", n: 3 },
  { img: "/landing/community/carote-cookware.webp", n: 4 },
];

const PLANS = [
  { id: "basic", descKey: "resellerLanding.planBasic", highlight: false },
  { id: "plus", descKey: "resellerLanding.planPlus", highlight: false },
  { id: "pro", descKey: "resellerLanding.planPro", highlight: true },
];

// The landing page never names this retailer, so its guide link gets a generic label.
const GUIDE_LABELS: Record<string, string> = { "home-depot-penny-items": "Big box penny items" };

const formatPrice = (price: number) => `$${Number.isInteger(price) ? price : price.toFixed(2)}`;

export default function ResellerLandingPage() {
  useEnforceLightMode();
  usePageBackground("#FFFFFF", "#FFFFFF");
  const { t, currentLanguage, setLanguage } = useTranslation();

  useEffect(() => {
    const utm = captureUtm();
    track("landing_page_viewed", { referrer: document.referrer, ...utm });
  }, []);

  const ctaClick = (location: string) => track("landing_cta_clicked", { location });

  const finds = FIND_KEYS.map((key) => t(key));
  const plans = PLANS.flatMap((plan) => {
    const config = LANDING_PAGE_PLANS.find((p) => p.id === plan.id);
    return config ? [{ ...plan, name: config.name, price: formatPrice(config.price) }] : [];
  });

  return (
    <div className="rl-page">
      <nav className="rl-nav" aria-label={t("resellerLanding.navLabel")}>
        <div className="rl-nav-left">
          <Link to="/" className="rl-brand">
            <img src="/app_icon.png" alt="" width={26} height={26} />
            DealHawk
          </Link>
          <div className="rl-nav-links">
            <a href="#how">{t("resellerLanding.navHow")}</a>
            <a href="#hauls">{t("resellerLanding.navHauls")}</a>
            <a href="#pricing">{t("resellerLanding.navPricing")}</a>
            <a href="#faq">{t("resellerLanding.navFaq")}</a>
          </div>
        </div>
        <div className="rl-nav-right">
          <Link to="/get-started" className="rl-login" onClick={() => ctaClick("nav_login")}>
            {t("resellerLanding.logIn")}
          </Link>
          <Link to="/get-started" className="rl-nav-cta" onClick={() => ctaClick("nav")}>
            {t("resellerLanding.getStarted")}
          </Link>
        </div>
      </nav>

      <main>
        <section id="top" className="rl-hero">
          <img
            className="rl-side rl-side-left rl-float"
            src="/landing/illustrations/n1-cart.webp"
            alt=""
            width={805}
            height={805}
          />
          <img
            className="rl-side rl-side-right rl-float2"
            src="/landing/illustrations/n7-scan.webp"
            alt=""
            width={796}
            height={796}
          />
          <h1 className="rl-h1 rl-up">
            {t("resellerLanding.heroTitle")
              .split(/(?<=\.)\s+/)
              .map((line) => (
                <span key={line} style={{ display: "block" }}>
                  {line}
                </span>
              ))}
          </h1>
          <p className="rl-lede rl-up rl-d1">{t("resellerLanding.heroLede")}</p>
          <div className="rl-hero-ctas rl-up rl-d2">
            <Link to="/get-started" className="rl-btn rl-btn-primary" onClick={() => ctaClick("hero_primary")}>
              {t("resellerLanding.heroCta")}
            </Link>
            <a href="#hauls" className="rl-btn rl-btn-soft" onClick={() => ctaClick("hero_hauls")}>
              {t("resellerLanding.heroHauls")}
            </a>
          </div>
          <p className="rl-stores rl-up rl-d3">{t("resellerLanding.heroStores")}</p>
          <div className="rl-ticker-wrap rl-up rl-d3">
            {/* The list runs twice so scrolling it by -50% loops seamlessly. */}
            <ul className="rl-ticker" aria-label={t("resellerLanding.findsLabel")}>
              {[...finds, ...finds].map((find, i) => (
                <li key={i} aria-hidden={i >= finds.length || undefined}>
                  {find}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <ResellerBento t={t} />

        <section id="how" className="rl-block">
          <h2 className="rl-h2">{t("resellerLanding.stepsTitle")}</h2>
          <div className="rl-steps">
            {STEPS.map((step) => (
              <div key={step.n} className="rl-step rl-reveal">
                <img src={step.img} alt="" width={805} height={805} loading="lazy" />
                <div className="rl-step-num" aria-hidden="true">
                  {step.n}
                </div>
                <h3>{t(`resellerLanding.step${step.n}Title`)}</h3>
                <p>{t(`resellerLanding.step${step.n}Text`)}</p>
              </div>
            ))}
          </div>
        </section>

        <section id="hauls" className="rl-block">
          <h2 className="rl-h2">{t("resellerLanding.haulsTitle")}</h2>
          <div className="rl-hauls">
            {HAULS.map((haul) => (
              <figure key={haul.n} className="rl-reveal">
                <img
                  src={haul.img}
                  alt={t(`resellerLanding.haul${haul.n}Alt`)}
                  width={900}
                  height={1200}
                  loading="lazy"
                />
                <figcaption>{t(`resellerLanding.haul${haul.n}Caption`)}</figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section className="rl-split">
          <div id="faq">
            <h2 className="rl-h2 rl-h2-sm">{t("resellerLanding.faqTitle")}</h2>
            <div className="rl-faq">
              {[1, 2, 3].map((n) => (
                <details key={n} open={n === 1}>
                  <summary>{t(`resellerLanding.faq${n}Q`)}</summary>
                  <p>{t(`resellerLanding.faq${n}A`)}</p>
                </details>
              ))}
            </div>
          </div>
          <div id="pricing">
            <h2 className="rl-h2 rl-h2-sm">{t("resellerLanding.pricingTitle")}</h2>
            <div className="rl-plans">
              {plans.map((plan) => (
                <div key={plan.id} className={`rl-plan rl-reveal${plan.highlight ? " rl-plan-pro" : ""}`}>
                  <div>
                    <div className="rl-plan-name">{plan.name}</div>
                    <div className="rl-plan-desc">{t(plan.descKey)}</div>
                  </div>
                  <div className="rl-plan-price">{plan.price}</div>
                </div>
              ))}
              <p className="rl-plans-explainer">{t("resellerLanding.pricingExplainer")}</p>
              <p className="rl-plans-note">{t("resellerLanding.pricingNote")}</p>
            </div>
          </div>
        </section>

        <section className="rl-closing">
          <img className="rl-float" src="/landing/illustrations/n8-map.webp" alt="" width={796} height={796} loading="lazy" />
          <h2>{t("resellerLanding.closingTitle")}</h2>
          <Link to="/get-started" className="rl-btn rl-btn-primary" onClick={() => ctaClick("bottom")}>
            {t("resellerLanding.getStarted")}
          </Link>
        </section>
        <p className="rl-fineprint">{t("resellerLanding.examplePrices")}</p>
      </main>

      <footer className="rl-footer">
        <div className="rl-footer-row">
          <nav aria-label={t("resellerLanding.footerLabel")}>
            <a href="/penny-meals/">Penny Meals</a>
            <Link to="/privacy">{t("landing.footerPrivacy")}</Link>
            <Link to="/terms">{t("landing.footerTerms")}</Link>
            <Link to="/support">{t("landing.footerSupport")}</Link>
            <a href={helpCenterUrl(currentLanguage)}>{t("support.videoTutorials")}</a>
          </nav>
          <div className="rl-lang" role="group" aria-label={t("resellerLanding.languageLabel")}>
            {(["en", "es"] as const).map((lang) => (
              <button
                key={lang}
                type="button"
                onClick={() => setLanguage(lang)}
                aria-pressed={currentLanguage === lang}
              >
                {lang.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
        {/* Crawlable links into the guides and public deal catalog: the homepage is the
            strongest page on the site, so these are how search engines find the rest. */}
        <nav aria-label="Guides and deals" className="rl-footer-row rl-footer-guides">
          <span className="rl-footer-links">
            {GUIDES.map((g) => (
              <Link key={g.slug} to={`/guides/${g.slug}`}>
                {GUIDE_LABELS[g.slug] ?? g.h1.split(":")[0]}
              </Link>
            ))}
            <a href="/shop">All price drops</a>
            <Link to="/deals">Online deals</Link>
            <Link to="/guides">Guides</Link>
          </span>
          <span>{t("landing.footerCopyright", { year: new Date().getFullYear() })}</span>
        </nav>
      </footer>
    </div>
  );
}
